// RouteToggle.tsx — the main ↔ self-compassion track switch. A quiet two-segment
// slider shown at the top of the programme section (on Today and Journey), so the
// gentler "soft landing" route is always one tap away. Switching changes the whole
// programme section; progress for each track is kept separate (ids never collide).
//
// `prominent` (used by the new Today header) lifts it onto a light card with a
// solid dark-green active button, so the choice stands out instead of blending in.
import { useProgress } from '@/hooks/useProgress';
import { Icon } from '@/components/icons';
import type { ProgrammeRoute } from '@/services/progress';

export function RouteToggle({ style, prominent = false }: { style?: React.CSSProperties; prominent?: boolean }) {
  const progress = useProgress();
  const value = progress.state.activeProgramme;
  const opt = (id: ProgrammeRoute, label: string) => {
    const on = value === id;
    return (
      <button
        type="button"
        onClick={() => progress.setActiveProgramme(id)}
        aria-pressed={on}
        className="elev-transition"
        style={
          prominent
            ? {
                flex: 1,
                padding: '10px 10px',
                borderRadius: 999,
                border: 'none',
                cursor: 'pointer',
                // active thumb = solid dark green, lifted; inactive = quiet
                background: on ? '#1f7d5e' : 'transparent',
                boxShadow: on ? '0 2px 8px rgba(31,125,94,0.30)' : 'none',
                color: on ? '#fff' : 'var(--ink-soft)',
                fontFamily: 'Inter, system-ui, sans-serif',
                fontSize: 12.5,
                fontWeight: on ? 700 : 600,
                whiteSpace: 'nowrap',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 5,
              }
            : {
                flex: 1,
                padding: '8px 10px',
                borderRadius: 999,
                border: 'none',
                cursor: 'pointer',
                background: on ? 'var(--card-raised-grad)' : 'transparent',
                boxShadow: on ? 'var(--edge-light), var(--lift-soft)' : 'none',
                color: on ? (id === 'compassion' ? 'var(--green-deep)' : 'var(--amber-deep)') : 'var(--ink-soft)',
                fontFamily: 'Inter, system-ui, sans-serif',
                fontSize: 12.5,
                fontWeight: on ? 700 : 600,
                whiteSpace: 'nowrap',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 5,
              }
        }
      >
        {id === 'compassion' && <Icon name="leaf" size={13} />}
        {label}
      </button>
    );
  };
  return (
    <div
      style={{
        display: 'flex',
        gap: 3,
        padding: prominent ? 4 : 3,
        borderRadius: 999,
        background: prominent ? '#FFFFFF' : 'var(--well)',
        boxShadow: prominent ? '0 4px 16px rgba(0,0,0,0.06)' : 'inset 0 1px 3px rgba(140,112,64,0.10)',
        ...style,
      }}
    >
      {opt('main', 'Main programme')}
      {opt('compassion', 'Self-compassion')}
    </div>
  );
}
